'use client';

import React, { useEffect, useState, useCallback } from 'react';
import Link from 'next/link';
import { AdminShell } from '@/components/shell/AdminShell';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { useAuth } from '@/context/AuthContext';
import { fetchFromAdminApi } from '@/lib/api-client';
import { AdminDashboardData, AdminAttentionItem } from '@/types/dashboard';

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState<AdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCorrelationId, setActiveCorrelationId] = useState<string | undefined>();
  const [attentionCategoryFilter, setAttentionCategoryFilter] = useState<string>('All');

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchFromAdminApi<AdminDashboardData>('/api/admin/dashboard');
      if (res.data) {
        setData(res.data);
        setActiveCorrelationId(res.correlationId);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load admin operations dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const metrics = data?.metrics;
  const needsAttention = data?.needsAttention ?? [];
  const recentActivity = data?.recentActivity ?? [];

  const filteredAttention = needsAttention.filter((item) => {
    if (attentionCategoryFilter === 'All') return true;
    return item.category.toLowerCase() === attentionCategoryFilter.toLowerCase();
  });

  const getSeverityVariant = (severity: string): 'danger' | 'warning' | 'neutral' => {
    switch (severity.toLowerCase()) {
      case 'critical':
      case 'error':
        return 'danger';
      case 'warning':
        return 'warning';
      default:
        return 'neutral';
    }
  };

  const getHealthVariant = (status: string): 'success' | 'warning' | 'danger' => {
    switch (status.toLowerCase()) {
      case 'healthy':
        return 'success';
      case 'degraded':
        return 'warning';
      default:
        return 'danger';
    }
  };

  return (
    <AdminShell currentCorrelationId={activeCorrelationId}>
      <div className="space-y-6">
        {/* Top Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-zinc-800">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold tracking-tight text-zinc-100">Operations Control Center</h1>
              <span className="text-[10px] uppercase font-mono px-2 py-0.5 rounded bg-zinc-800 text-emerald-400 border border-zinc-700">
                Live Operations
              </span>
            </div>
            <p className="text-xs text-zinc-400 font-mono mt-1">
              Cross-tenant administration, pipeline health, credit settlements, and operational diagnostics.
            </p>
          </div>
          <div className="flex items-center gap-3 font-mono text-xs">
            <button
              onClick={loadDashboard}
              disabled={loading}
              className="px-2.5 py-1 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 rounded border border-zinc-800 transition-colors disabled:opacity-50"
            >
              {loading ? 'Refreshing...' : '↻ Refresh Data'}
            </button>
            {activeCorrelationId && (
              <span className="text-[10px] text-zinc-500 hidden md:inline">
                CID: {activeCorrelationId.substring(0, 8)}...
              </span>
            )}
          </div>
        </div>

        {/* Error notification when data exists but refresh failed */}
        {error && data && (
          <div className="p-3 bg-rose-950/40 border border-rose-900 rounded text-xs font-mono text-rose-300">
            {error}
          </div>
        )}

        {/* Clean operational error state when API is unreachable */}
        {error && !data ? (
          <div className="p-8 my-10 bg-zinc-900/60 border border-zinc-800 rounded-lg text-center max-w-md mx-auto">
            <div className="w-10 h-10 rounded-full bg-rose-950/80 border border-rose-900/80 text-rose-400 flex items-center justify-center mx-auto mb-3">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-sm font-semibold text-zinc-100 mb-1">Unable to connect to Admin API</h2>
            <p className="text-xs text-zinc-400 mb-5 font-mono">
              The Admin API is currently unreachable.
            </p>
            <button
              onClick={loadDashboard}
              className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold rounded text-xs transition-colors"
            >
              Retry
            </button>
          </div>
        ) : loading && !data ? (
          <div className="p-12 text-center text-xs font-mono text-zinc-400 flex items-center justify-center gap-2">
            <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            <span>Loading operations dashboard...</span>
          </div>
        ) : (
          <>
        {/* Quick Module Navigation Strip */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 text-xs font-mono text-zinc-400 scrollbar-none">
          <span className="text-[11px] uppercase tracking-wider text-zinc-500 shrink-0 mr-1">Modules:</span>
          <Link href="/users" className="px-2.5 py-1 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 rounded border border-zinc-800 transition-colors shrink-0">
            Users
          </Link>
          <Link href="/organizations" className="px-2.5 py-1 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 rounded border border-zinc-800 transition-colors shrink-0">
            Organizations
          </Link>
          <Link href="/brands" className="px-2.5 py-1 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 rounded border border-zinc-800 transition-colors shrink-0">
            Brands & Onboarding
          </Link>
          <Link href="/credits" className="px-2.5 py-1 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 rounded border border-zinc-800 transition-colors shrink-0">
            Credits & Wallets
          </Link>
          <Link href="/jobs" className="px-2.5 py-1 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 rounded border border-zinc-800 transition-colors shrink-0">
            AI Jobs & Recon
          </Link>
          <Link href="/campaigns" className="px-2.5 py-1 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 rounded border border-zinc-800 transition-colors shrink-0">
            Campaigns & Publishing
          </Link>
          <Link href="/logs" className="px-2.5 py-1 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 rounded border border-zinc-800 transition-colors shrink-0">
            Application Logs
          </Link>
          <Link href="/audit" className="px-2.5 py-1 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 rounded border border-zinc-800 transition-colors shrink-0">
            Admin Audit
          </Link>
          <Link href="/health" className="px-2.5 py-1 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 rounded border border-zinc-800 transition-colors shrink-0">
            System Health
          </Link>
        </div>

        {/* Operational KPI Strip */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {/* Card 1: Users & Orgs */}
          <div className="bg-zinc-900/80 border border-zinc-800 rounded-lg p-3 font-mono">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Tenants & Users</div>
            <div className="text-base font-bold text-zinc-100">
              {metrics ? `${metrics.totalUsers} / ${metrics.totalOrganizations}` : '...'}
            </div>
            <div className="text-[11px] text-zinc-400 mt-1 flex justify-between">
              <span>Users / Orgs</span>
              <Link href="/users" className="text-emerald-400 hover:underline">View ↗</Link>
            </div>
          </div>

          {/* Card 2: Brands & Onboarding */}
          <div className="bg-zinc-900/80 border border-zinc-800 rounded-lg p-3 font-mono">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Active Brands</div>
            <div className="text-base font-bold text-zinc-100 flex items-center justify-between">
              <span>{metrics ? metrics.activeBrands : '...'}</span>
              {metrics && metrics.onboardingFailures > 0 && (
                <span className="text-[10px] text-rose-400 font-semibold bg-rose-950/60 px-1 rounded">
                  {metrics.onboardingFailures} fail
                </span>
              )}
            </div>
            <div className="text-[11px] text-zinc-400 mt-1 flex justify-between">
              <span>Onboarding health</span>
              <Link href="/brands" className="text-emerald-400 hover:underline">View ↗</Link>
            </div>
          </div>

          {/* Card 3: Credits Pool */}
          <div className="bg-zinc-900/80 border border-zinc-800 rounded-lg p-3 font-mono">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Available Credits</div>
            <div className="text-base font-bold text-emerald-400">
              {metrics ? metrics.availableCredits.toLocaleString() : '...'}
            </div>
            <div className="text-[11px] text-zinc-400 mt-1 flex justify-between">
              <span className="text-zinc-500">Resv: {metrics ? metrics.reservedCredits.toLocaleString() : '...'}</span>
              <Link href="/credits" className="text-emerald-400 hover:underline">Ledger ↗</Link>
            </div>
          </div>

          {/* Card 4: AI Jobs Today */}
          <div className="bg-zinc-900/80 border border-zinc-800 rounded-lg p-3 font-mono">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">AI Jobs Today</div>
            <div className="text-base font-bold text-zinc-100 flex items-center justify-between">
              <span>{metrics ? metrics.aiJobsToday : '...'}</span>
              {metrics && metrics.failedAiJobs > 0 && (
                <span className="text-[10px] text-rose-400 font-semibold bg-rose-950/60 px-1 rounded">
                  {metrics.failedAiJobs} err
                </span>
              )}
            </div>
            <div className="text-[11px] text-zinc-400 mt-1 flex justify-between">
              <span className="text-zinc-500">Pending: {metrics ? metrics.completionPendingCount : '0'}</span>
              <Link href="/jobs" className="text-emerald-400 hover:underline">Pipeline ↗</Link>
            </div>
          </div>

          {/* Card 5: Campaigns & Channels */}
          <div className="bg-zinc-900/80 border border-zinc-800 rounded-lg p-3 font-mono">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Publishing Active</div>
            <div className="text-base font-bold text-zinc-100 flex items-center justify-between">
              <span>{metrics ? metrics.campaignsActive : '...'}</span>
              {metrics && metrics.disconnectedOrExpiredChannels > 0 && (
                <span className="text-[10px] text-amber-400 font-semibold bg-amber-950/60 px-1 rounded">
                  {metrics.disconnectedOrExpiredChannels} ch!
                </span>
              )}
            </div>
            <div className="text-[11px] text-zinc-400 mt-1 flex justify-between">
              <span className="text-zinc-500">Failed jobs: {metrics ? metrics.failedPublishJobs : '0'}</span>
              <Link href="/campaigns" className="text-emerald-400 hover:underline">Social ↗</Link>
            </div>
          </div>

          {/* Card 6: System Health & Errors */}
          <div className="bg-zinc-900/80 border border-zinc-800 rounded-lg p-3 font-mono">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">System Health</div>
            <div className="text-base font-bold flex items-center justify-between">
              <span className={metrics?.systemHealthStatus === 'Healthy' ? 'text-emerald-400' : 'text-amber-400'}>
                {metrics ? metrics.systemHealthStatus : 'Probing...'}
              </span>
              <StatusBadge
                status={metrics?.systemHealthStatus ?? 'PROBING'}
                variant={getHealthVariant(metrics?.systemHealthStatus ?? 'Healthy')}
                size="sm"
              />
            </div>
            <div className="text-[11px] text-zinc-400 mt-1 flex justify-between">
              <span className="text-zinc-500">Errors 24h: {metrics ? metrics.applicationErrorsLast24Hours : '...'}</span>
              <Link href="/health" className="text-emerald-400 hover:underline">Checks ↗</Link>
            </div>
          </div>
        </div>

        {/* Needs Attention Section */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-zinc-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 font-mono">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse" />
              <h2 className="text-sm font-semibold text-zinc-100">Operational Items Requiring Attention</h2>
              <span className="text-xs text-zinc-500">({filteredAttention.length})</span>
            </div>

            {/* Category Filter Pills */}
            <div className="flex flex-wrap gap-1 text-[11px]">
              {['All', 'AI Jobs', 'Onboarding', 'Publishing', 'Channels', 'Logs', 'Reconciliation'].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setAttentionCategoryFilter(cat)}
                  className={`px-2 py-0.5 rounded transition-colors ${
                    attentionCategoryFilter === cat
                      ? 'bg-zinc-800 text-zinc-100 font-semibold border border-zinc-700'
                      : 'text-zinc-500 hover:text-zinc-300'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="divide-y divide-zinc-800/60 font-mono text-xs">
            {loading ? (
              <div className="p-6 text-center text-zinc-500">Evaluating telemetry and subsystem attention items...</div>
            ) : filteredAttention.length === 0 ? (
              <div className="p-8 text-center text-zinc-500">
                ✓ No critical operational anomalies detected matching filter.
              </div>
            ) : (
              filteredAttention.map((item) => (
                <div key={item.id} className="p-3.5 hover:bg-zinc-850/50 transition-colors flex items-start justify-between gap-4">
                  <div className="space-y-1 max-w-3xl">
                    <div className="flex items-center gap-2">
                      <StatusBadge status={item.severity} variant={getSeverityVariant(item.severity)} size="sm" />
                      <span className="text-[10px] bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded border border-zinc-700/80">
                        {item.category}
                      </span>
                      <span className="font-semibold text-zinc-100">{item.title}</span>
                      {item.organizationName && (
                        <span className="text-zinc-500 text-[11px]">[{item.organizationName}]</span>
                      )}
                    </div>
                    <p className="text-zinc-400 text-[11px] leading-relaxed pl-1">{item.detail}</p>
                    <div className="text-[10px] text-zinc-500 pl-1">
                      Detected: {new Date(item.timestamp).toLocaleString()} {item.entityId ? `• Entity: ${item.entityId}` : ''}
                    </div>
                  </div>

                  <Link
                    href={item.targetUrl}
                    className="shrink-0 px-2.5 py-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded text-[11px] transition-colors border border-zinc-700"
                  >
                    Investigate ↗
                  </Link>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Lower Row: Recent Governance Activity + System Diagnostics */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 font-mono text-xs">
          {/* Recent Governance / Admin Audit (2 cols) */}
          <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-emerald-400 font-semibold">⚡ Recent Administrative Activity</span>
                <span className="text-zinc-500 text-[11px]">(Governance Log)</span>
              </div>
              <Link href="/audit" className="text-[11px] text-zinc-400 hover:text-zinc-200 underline">
                View Full Audit Log ↗
              </Link>
            </div>

            <div className="divide-y divide-zinc-800/60">
              {recentActivity.length === 0 ? (
                <div className="p-6 text-center text-zinc-500">No recent administrative mutations recorded.</div>
              ) : (
                recentActivity.map((act) => (
                  <div key={act.id} className="p-3 hover:bg-zinc-850/50 transition-colors flex items-center justify-between gap-3">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-zinc-200">{act.action}</span>
                        <StatusBadge
                          status={act.result}
                          variant={act.result === 'Success' ? 'success' : 'danger'}
                          size="sm"
                        />
                      </div>
                      <div className="text-[11px] text-zinc-400 truncate max-w-md">
                        {act.actor} • {act.reason}
                      </div>
                    </div>
                    <div className="text-right text-[10px] text-zinc-500 whitespace-nowrap">
                      {new Date(act.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* System Telemetry & Health Card (1 col) */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
              <span className="font-semibold text-zinc-200">System Telemetry</span>
              <StatusBadge
                status={metrics?.systemHealthStatus ?? 'PROBING'}
                variant={getHealthVariant(metrics?.systemHealthStatus ?? 'Healthy')}
                size="sm"
              />
            </div>

            <div className="space-y-2.5 text-[11px] text-zinc-400">
              <div>
                <span className="text-zinc-500">Summary:</span>
                <div className="text-zinc-200 mt-0.5">{metrics?.systemHealthSummary || 'Probing subsystem health status...'}</div>
              </div>
              <div>
                <span className="text-zinc-500">Error Frequency (24h):</span>
                <div className="text-zinc-200 mt-0.5">{metrics?.applicationErrorsLast24Hours ?? 0} application errors logged</div>
              </div>
              <div>
                <span className="text-zinc-500">Security Boundary:</span>
                <div className="text-zinc-200 mt-0.5">Deny-by-default with fine-grained claim evaluation</div>
              </div>
              <div>
                <span className="text-zinc-500">Credit Grant Logic:</span>
                <div className="text-zinc-200 mt-0.5">Delegated to trusted legacy ICreditGrantService</div>
              </div>
            </div>

            <div className="pt-2 border-t border-zinc-800">
              <Link
                href="/health"
                className="w-full block text-center py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded text-xs transition-colors border border-zinc-700"
              >
                Inspect Subsystem Probes ↗
              </Link>
            </div>
          </div>
        </div>
        </>
        )}
      </div>
    </AdminShell>
  );
}
